import { useEffect, useRef, useState } from 'react';
import api from '../../../../api';
import { useActionLock } from '../../../../hooks/useActionLock';
import useFlashMessage from '../../../../hooks/useFlashMessage';
const mimePermitidos = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export function useArquivosModule({ documento, permissoes, bloqueado, somenteConsulta = false }) {
    const [lista, setLista] = useState([]),
        [files, setFiles] = useState([]),
        [descricao, setDescricao] = useState('');
    const [apenasSocio, setApenasSocio] = useState(false);
    const [editando, setEditando] = useState(null),
        [selecionado, setSelecionado] = useState(null),
        [preview, setPreview] = useState(null);
    const [carregando, setCarregando] = useState(false),
        [erro, setErro] = useState('');
    const contexto = useRef(0),
        consulta = useRef(0),
        urlRef = useRef(null);
    const { isLocked, run } = useActionLock(),
        { setFlashMessage } = useFlashMessage();
    const base = `/arquivoDocumento/${documento.uuid}/arquivos`;
    const ocupado = bloqueado || carregando || isLocked;
    const pendente = Boolean(files.length || editando || descricao || apenasSocio);
    const podeAnexar =
        !somenteConsulta && permissoes.autorizado && permissoes.editarItem && Boolean(documento.uuid) && Boolean(documento.ativo);
    const fecharPreview = () => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
        setPreview(null);
    };
    const carregar = async () => {
        if (!documento.uuid || !permissoes.autorizado) return;
        const atual = ++consulta.current,
            ctx = contexto.current;
        setCarregando(true);
        setErro('');
        try {
            const { data } = await api.get(base);
            if (ctx !== contexto.current || atual !== consulta.current) return;
            if (!Array.isArray(data)) throw new Error('Lista de anexos inválida.');
            setLista(data);
        } catch (e) {
            if (ctx === contexto.current && atual === consulta.current) setErro(e.response?.data?.message || e.message);
        } finally {
            if (ctx === contexto.current && atual === consulta.current) setCarregando(false);
        }
    };
    useEffect(() => {
        contexto.current += 1;
        consulta.current += 1;
        setLista([]);
        setFiles([]);
        setDescricao('');
        setApenasSocio(false);
        setEditando(null);
        setSelecionado(null);
        setErro('');
        setCarregando(false);
        fecharPreview();
        carregar();
        return () => {
            contexto.current += 1;
            consulta.current += 1;
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documento.uuid, permissoes.autorizado]);
    const limpar = () => {
        if (ocupado) return;
        setFiles([]);
        setDescricao('');
        setApenasSocio(false);
        setEditando(null);
    };
    const selecionarArquivos = (e) => {
        const itens = Array.from(e.target.files || []);
        e.target.value = '';
        if (ocupado || !podeAnexar || editando) return;
        if (!itens.length) return;
        if (itens.length > 10 || itens.some((f) => f.size < 1 || f.size > 15 * 1024 * 1024 || !mimePermitidos.includes(f.type))) {
            setFlashMessage('Selecione até 10 arquivos PDF, JPG, PNG, GIF ou WEBP, com até 15 MB cada.', 'warning');
            return;
        }
        setFiles(itens);
    };
    const executar = (fn) => {
        if (ocupado || !documento.uuid || !permissoes.autorizado) return;
        const ctx = contexto.current;
        return run(async () => {
            try {
                await fn(ctx);
            } catch (e) {
                if (ctx !== contexto.current) return;
                let mensagem = e.response?.data?.message || e.message;
                if (typeof Blob !== 'undefined' && e.response?.data instanceof Blob) {
                    try {
                        mensagem = JSON.parse(await e.response.data.text()).message || mensagem;
                    } catch {
                        /* mantém a mensagem original */
                    }
                }
                if (ctx === contexto.current) setFlashMessage(mensagem || 'Erro ao processar anexo.', 'error');
            }
        });
    };
    const salvar = () =>
        executar(async (ctx) => {
            if (somenteConsulta || (editando ? !permissoes.editarItem : !podeAnexar)) return;
            let response;
            if (editando) response = await api.post(`${base}/${editando.uuid}/editar`, { descricao, apenasSocio });
            else {
                if (!files.length) {
                    setFlashMessage('Selecione os arquivos.', 'warning');
                    return;
                }
                const fd = new FormData();
                files.forEach((f) => fd.append('arquivo', f));
                fd.append('descricao', descricao);
                fd.append('apenasSocio', String(apenasSocio));
                response = await api.post(base, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            if (ctx !== contexto.current) return;
            if (response.data.type !== 'success') throw new Error(response.data.message || 'Não foi possível salvar.');
            setFiles([]);
            setDescricao('');
            setApenasSocio(false);
            setEditando(null);
            setFlashMessage(response.data.message, 'success');
            await carregar();
        });
    const editar = (row) => {
        if (somenteConsulta || ocupado || !permissoes.editarItem) return;
        if (pendente) {
            setFlashMessage('Conclua ou descarte a edição atual.', 'warning');
            return;
        }
        setEditando(row);
        setDescricao(row.descricao || '');
        setApenasSocio([true, 1, '1', 'true'].includes(row.apenasSocio));
    };
    const excluir = () =>
        executar(async (ctx) => {
            if (somenteConsulta || !permissoes.cancelarItem || !selecionado || pendente) return;
            const { data } = await api.post(`${base}/${selecionado.uuid}/excluir`, {});
            if (ctx !== contexto.current) return;
            if (data.type !== 'success') throw new Error(data.message || 'Não foi possível excluir.');
            setSelecionado(null);
            setFlashMessage(data.message, 'success');
            await carregar();
        });
    const abrir = (row, baixar = false, { novaAba = false } = {}) => {
        if (ocupado || !documento.uuid || !permissoes.autorizado || !permissoes.imprimirItem) return;
        // Reserva a aba durante o clique, antes da requisição autenticada.
        let janela = null;
        const ehPdf =
            String(row.mimeType || '')
                .split(';')[0]
                .trim()
                .toLowerCase() === 'application/pdf' || /\.pdf$/i.test(row.nomeOriginal || row.arquivo || '');
        if (novaAba && !baixar && ehPdf) {
            try {
                janela = window.open('about:blank', '_blank');
                if (janela) {
                    janela.opener = null;
                    janela.document.title = 'Carregando documento';
                    janela.document.body.textContent = 'Carregando documento...';
                }
            } catch {
                if (janela) janela.close();
                janela = null;
            }
        }
        return executar(async (ctx) => {
            try {
                const { data } = await api.get(`${base}/${row.uuid}/${baixar ? 'baixar' : 'abrir'}`, { responseType: 'blob' });
                if (ctx !== contexto.current) return;
                const nome = row.nomeOriginal || row.arquivo || 'documento';
                const tipoInformado = String(row.mimeType || data?.type || '')
                    .split(';')[0]
                    .trim()
                    .toLowerCase();
                const mimeType = mimePermitidos.includes(tipoInformado)
                    ? tipoInformado
                    : /\.pdf$/i.test(nome)
                      ? 'application/pdf'
                      : 'application/octet-stream';
                const blob = data instanceof Blob && data.type === mimeType ? data : new Blob([data], { type: mimeType });
                const url = URL.createObjectURL(blob);
                if (baixar) {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = nome;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                } else {
                    fecharPreview();
                    urlRef.current = url;
                    setPreview({ url, nome, mimeType });
                    if (janela && !janela.closed) {
                        try {
                            janela.location.replace(url);
                            janela = null;
                        } catch {
                            /* O diálogo mantém as opções Abrir e Baixar. */
                        }
                    }
                }
            } finally {
                // Não deixa uma aba em branco após erro, troca de documento ou cancelamento.
                if (janela && !janela.closed) janela.close();
            }
        });
    };
    const alterarDescricao = (value) => {
        if (!somenteConsulta && !ocupado && (editando ? permissoes.editarItem : podeAnexar)) setDescricao(value);
    };
    const alterarApenasSocio = (value) => {
        if (!somenteConsulta && !ocupado && (editando ? permissoes.editarItem : podeAnexar)) setApenasSocio(value === true);
    };
    return {
        lista,
        files,
        apenasSocio,
        alterarApenasSocio,
        descricao,
        alterarDescricao,
        editando,
        selecionado,
        setSelecionado,
        preview,
        fecharPreview,
        carregando,
        erro,
        ocupado,
        trabalhando: isLocked,
        pendente,
        podeAnexar,
        selecionarArquivos,
        limpar,
        salvar,
        editar,
        excluir,
        abrir,
        recarregar: carregar,
    };
}
